import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { AuthenticationService, LoginContext } from '@app/auth';
import { ErrorMessage } from '@shared/http/http.service';
import { I18nService } from '@app/i18n';
import { ButtonPadding, ButtonVariant, PlButtonType } from '@app/components/button/button.component';
import { HttpErrorResponse } from '@angular/common/http';
import { PlInputComponent, PlInputType } from '@app/components/pl-input/pl-input.component';
import { getRoutePath, PlRoute } from '@app/utils/routes';

enum FormControlName {
  username = 'username',
  password = 'password',
}

interface LoginForm {
  [FormControlName.username]: FormControl<string | null>;
  [FormControlName.password]: FormControl<string | null>;
}

export interface QueryParamsLogin {
  redirect?: string;
  errorMessage?: ErrorMessage;
}

@Component({
  selector: 'pl-login',
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit {
  public error: string | undefined;
  public loginForm!: FormGroup<LoginForm>;
  public isLoading: boolean = false;

  protected readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  protected readonly FormControlName: typeof FormControlName = FormControlName;
  protected readonly PlButtonType: typeof PlButtonType = PlButtonType;
  protected readonly PlInputType: typeof PlInputType = PlInputType;

  @ViewChild('plInputUsername') public plInputUsername!: PlInputComponent;
  @ViewChild('plInputPassword') public plInputPassword!: PlInputComponent;

  constructor(
    private authenticationService: AuthenticationService,
    private i18nService: I18nService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.createForm();
  }

  public ngOnInit(): void {
    const errorMessage: ErrorMessage = this.route.snapshot.queryParams['errorMessage'];
    if (errorMessage === ErrorMessage.tokenExpired) {
      this.error = this.i18nService.translate('errors.token_expired');
    } else if (errorMessage === ErrorMessage.internalServerError || errorMessage === ErrorMessage.unknownError) {
      this.error = this.i18nService.translate('login.errors.auth_error');
    } else if (errorMessage === ErrorMessage.tooManyLoginAttempts) {
      this.error = this.i18nService.translate('login.errors.too_many_login_error');
    } else if (errorMessage) {
      this.error = this.i18nService.translate('errors.login_failed_error');
    }
  }

  public login(): void {
    this.updateLoginInputFromAutocomplete();
    if (!this.loginForm.valid) {
      this.loginForm.controls.username.markAsDirty();
      this.loginForm.controls.password.markAsDirty();
    } else {
      this.isLoading = true;

      const loginContext: LoginContext = {
        username: this.loginForm.controls.username.value as string,
        password: this.loginForm.controls.password.value as string,
      };
      this.authenticationService
        .login(loginContext)
        .pipe(
          finalize((): void => {
            this.loginForm.markAsPristine();
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (isUserLoggedInOnCourseWebsite: boolean): void => {
            if (isUserLoggedInOnCourseWebsite) {
              this.router.navigate([this.route.snapshot.queryParams['redirect'] || '/'], { replaceUrl: true });
            }
          },
          error: (error: HttpErrorResponse): void => {
            console.error(`There was an error during login: `, error);
            if (error && error.status === 401) {
              this.error = this.i18nService.translate('login.errors.invalid_credentials');
            } else if (error && error.status === 429) {
              this.error = this.i18nService.translate('login.errors.too_many_login_error');
            } else {
              this.error = this.i18nService.translate('login.errors.auth_error');
            }
          },
        });
    }
  }

  private createForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.loginForm = fb.nonNullable.group<LoginForm>({
      username: fb.control<string>('', [Validators.required]),
      password: fb.control<string>('', [Validators.required]),
    });
  }

  // When firefox autocomplete the input / password, no events is sent to angular,
  // so the user sees a value in the input/password but angular is not aware
  // This is a HACK to take the inner HTML value and update angular manually
  private updateLoginInputFromAutocomplete(): void {
    const angularUsernameValue: string | null = this.loginForm.controls.username.value;
    if (!angularUsernameValue) {
      const innerHTMLUsernameValue: string = this.plInputUsername.inputElement.nativeElement.value;

      if (innerHTMLUsernameValue) {
        this.loginForm.controls.username.setValue(innerHTMLUsernameValue);
        this.loginForm.controls.username.markAsDirty();
      }
    }

    const angularPasswordValue: string | null = this.loginForm.controls.password.value;
    if (!angularPasswordValue) {
      const innerHTMLPasswordValue: string = this.plInputPassword.inputElement.nativeElement.value;

      if (innerHTMLPasswordValue) {
        this.loginForm.controls.password.setValue(innerHTMLPasswordValue);
        this.loginForm.controls.password.markAsDirty();
      }
    }
  }

  public showForgotPasswordScreen(): void {
    this.router.navigate([getRoutePath(PlRoute.retrieveCredentials)]);
  }
}
